import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFederations } from '../store/actions/FederationsActions';

/**
 * Hook personalizzato per gestire le federazioni
 * Carica automaticamente i dati al primo utilizzo e li mantiene in cache
 */
export const useFederations = () => {
    const dispatch = useDispatch();
    const { federations, loading, error, lastFetched } = useSelector(state => state.federations);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Funzione per caricare le federazioni
    const loadFederations = useCallback(async (forceRefresh = false) => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (!tokenDetailsString) {
            console.warn('Token non trovato in sessionStorage');
            return null;
        }

        try {
            const tokenDetails = JSON.parse(tokenDetailsString);
            const token = tokenDetails.idToken;
            
            if (!token) {
                console.warn('Token non valido');
                return null;
            }

            const result = await dispatch(fetchFederations(token, forceRefresh));
            return result;
        } catch (error) {
            console.error('Errore durante il caricamento delle federazioni:', error);
            return null;
        }
    }, [dispatch]);

    // Caricamento automatico al primo utilizzo
    useEffect(() => {
        if (!hasInitialized && federations.length === 0 && !loading) {
            setHasInitialized(true);
            loadFederations();
        }
    }, [hasInitialized, federations.length, loading, loadFederations]);

    // Funzione per ottenere un organismo per codice
    const getFederationByCode = useCallback((code) => {
        return federations.find(federation => federation.code === code) || null;
    }, [federations]);

    // Funzione per ottenere federazioni per sport
    const getFederationsBySport = useCallback((sport) => {
        return federations.filter(federation => 
            federation.sports && federation.sports.includes(sport)
        );
    }, [federations]);

    // Funzione per verificare se i dati sono aggiornati (meno di 1 ora)
    const isDataFresh = useCallback(() => {
        if (!lastFetched) return false;
        const oneHour = 60 * 60 * 1000;
        return (Date.now() - lastFetched) < oneHour;
    }, [lastFetched]);

    return {
        federations,
        loading,
        error,
        lastFetched,
        loadFederations,
        getFederationByCode,
        getFederationsBySport,
        isDataFresh,
        hasData: federations.length > 0
    };
};